import { useEffect, useState } from 'react';
import { FormikHelpers } from 'formik';
import * as yup from 'yup';

import FormSubmitStatus from 'components/FormSubmitStatus';
import { Button } from 'components/Input/Button';
import InputField from 'components/Input/InputField/InputField';

import { PlainTextMessage } from 'routes/Chat/Messages/PlainTextMessage';
import WithDelayedAnimation from 'routes/Chat/Messages/WithDelayedAnimation/WithDelayedAnimation';

import { useAuthentication } from 'hooks/authentication';
import { useConversation } from 'hooks/conversation';
import { useCustomer } from 'hooks/customer';
import { useVendorCommand } from 'hooks/useVendorCommand';

import { getCjsDataAsync } from 'utils/cjs';
import { DataDogLogger } from 'utils/dataDog';
import { getFirstName, getLastName } from 'utils/name';
import { formatPhoneNumber, unformattedPhoneNumber } from 'utils/phone';
import { isLastMessage, isMessageRead } from 'utils/twilio';

import { MESSAGE_ALIGN } from 'routes/Chat/Messages/constants';
import { ACTIONS } from 'constants/hook';
import { VENDOR_COMMANDS } from 'constants/vendor-commands';

import { validationSchemaName, validationSchemaQuestion } from '../../formValidations';
import { CONTACT_METHOD_TYPE, PROMPTS, SUBMITTED } from '../constants';
import {
  ButtonGroup,
  RatingContactFollowUpForm,
  Section,
  Sections,
  SubmitSection,
} from '../styles';
import { RatingContactFollowUpProps, RatingContactFollowUpValuesType } from '../types';
import { htmlId } from 'utils/html';
import FormWrapper from 'components/FormWrapper';
import { FORM_IDS } from 'components/FormWrapper/constants';

const validationSchema = yup.object({
  question: validationSchemaQuestion,
  name: validationSchemaName,
});

/**
 * To force trigger in conversation (see Message Templates section in README for more info):
 * message_template_rating_contact_followup_chat_only_question_form *
 */
const ContactFollowUpChatOnly = (props: RatingContactFollowUpProps) => {
  const { authenticationState } = useAuthentication();
  const { conversationState } = useConversation();
  const { dispatchCustomerAction } = useCustomer();
  const { runVendorCommandAsync } = useVendorCommand();

  const { twilioMessageIndex, form } = props;
  const { isCompleted: previouslyCompleted, info } = form;
  const { name = '', question = '', contact_type, phone_number: phone_number = '' } = info ?? {};

  const [nameToInitialize, setNameToInitialize] = useState(name);
  const [phoneNumber, setPhoneNumber] = useState(phone_number);
  const [hasCjsName, setHasCjsName] = useState(false);

  const isLatestMessage = isLastMessage(twilioMessageIndex, conversationState.messages.length);
  const read = isMessageRead(twilioMessageIndex, conversationState);
  const animate = isLatestMessage && !read;
  const commonAnimationProps = { animate, showIndicator: true };

  useEffect(() => {
    if (previouslyCompleted) return;

    const cjs = async () => {
      const data = await getCjsDataAsync(authenticationState.propel, conversationState);
      if (data && data.firstName && !name) {
        setHasCjsName(true);
        setNameToInitialize(data.firstName);
      }

      if (data && data.phoneNumber && !phoneNumber) {
        setPhoneNumber(data.phoneNumber);
      }
    };

    cjs();
  }, []);

  const onSubmit = async (
    values: RatingContactFollowUpValuesType,
    helpers: FormikHelpers<RatingContactFollowUpValuesType>,
  ) => {
    helpers.setSubmitting(true);
    await runVendorCommandAsync(VENDOR_COMMANDS.CARVANA_CONTACT_FOLLOW_UP_QUESTION, {
      form_id: props.form.id,
      twilio_message_id: conversationState.messages[twilioMessageIndex].sid,
      question: values.question,
      contact_type: CONTACT_METHOD_TYPE.CHAT,
      phone_number: unformattedPhoneNumber(phone_number),
      name: values.name,
    });
    helpers.setSubmitting(false);
    helpers.setStatus(SUBMITTED);

    dispatchCustomerAction({
      type: ACTIONS.CHANGE,
      payload: {
        userInfo: { firstName: getFirstName(values.name), lastName: getLastName(values.name) },
      },
    });
  };

  const onSubmitError = (error: Error, helpers: FormikHelpers<RatingContactFollowUpValuesType>) => {
    helpers.setSubmitting(false);

    helpers.setStatus(error.message);

    DataDogLogger.error(error);
    console.error(error);
  };

  return (
    <FormWrapper
      id={FORM_IDS.RATING_CONTACT_FOLLOW_UP_CHAT_ONLY}
      initialValues={{
        question,
        contactMethod: contact_type || '',
        name: nameToInitialize,
        phone: formatPhoneNumber(phone_number),
      }}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onError={onSubmitError}
      validateOnBlur={false}
    >
      {({ isValid, isValidating, isSubmitting, status, dirty }) => {
        const fieldsDisabled = previouslyCompleted || status === SUBMITTED || isSubmitting;

        const submitDisabled = fieldsDisabled || !isValid || isValidating || !dirty;

        return (
          <RatingContactFollowUpForm noValidate>
            <Sections>
              <Section>
                <WithDelayedAnimation relativeIndex={0} {...commonAnimationProps}>
                  <PlainTextMessage
                    align={MESSAGE_ALIGN.LEFT}
                    hasTopGutter={false}
                    text={PROMPTS.QUESTION_PROMPT}
                  />
                </WithDelayedAnimation>
                <WithDelayedAnimation relativeIndex={1} {...commonAnimationProps}>
                  <Section>
                    <InputField
                      id={`question-${twilioMessageIndex}`}
                      name='question'
                      placeholder='Write your question here...'
                      multiline
                      required
                      disabled={fieldsDisabled}
                    />
                    {!hasCjsName && (
                      <InputField
                        id={`name-${twilioMessageIndex}`}
                        name='name'
                        placeholder='Name'
                        label='Name'
                        disabled={fieldsDisabled}
                      />
                    )}
                  </Section>
                </WithDelayedAnimation>
              </Section>
            </Sections>

            <SubmitSection>
              {previouslyCompleted || status ? (
                <FormSubmitStatus isSuccess={previouslyCompleted || status === SUBMITTED} />
              ) : (
                <div />
              )}
              {!previouslyCompleted && (
                <WithDelayedAnimation relativeIndex={3} animate={animate} showIndicator={false}>
                  <ButtonGroup>
                    <Button
                      id={htmlId('btn-rating-contact-follow-up-chat-only-submit')}
                      type='submit'
                      disabled={submitDisabled}
                    >
                      Submit
                    </Button>
                  </ButtonGroup>
                </WithDelayedAnimation>
              )}
            </SubmitSection>
          </RatingContactFollowUpForm>
        );
      }}
    </FormWrapper>
  );
};

export default ContactFollowUpChatOnly;
